import React, { useEffect, useMemo, useState } from 'react';
import Product from '../../Types/Product';
import { translate } from '../../Services/translation';
import ProductCardPlaceholder from '../ProductCards/ProductCardPlaceholder';
import Icon from '../Icon';
import WallpaperSimulatorFilterModal from './WallpaperSimulatorFilterModal';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Pagination from '../Pagination';
import { Pager, calculatePager } from '../../Services/Pagination.service';
import { useTypedDispatch } from '../../Hooks/useTypedDispatch';
import { queryProductFiltering } from '../../Actions/ProductFiltering.action';
import ProductCardImageSlider from '../ProductCards/ProductCardImageSlider';
import ActiveFilterChips from '../ActiveFilterChips/ActiveFilterChips';

interface ProductSelectorInterface {
  products?: Product[];
  setSelectedProduct: (product: Product) => void;
  totalProductsCount: number;
  currentPage: number;
  pageSize: number;
}

const WallpaperSimulatorProductSelector = ({
  products,
  setSelectedProduct,
  totalProductsCount,
  currentPage,
  pageSize,
}: ProductSelectorInterface) => {
  const dispatch = useTypedDispatch();
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [pager, setPager] = useState<Pager | undefined>();
  const { filters = [] } = useTypedSelector(
    (state) => state.productFiltering
  );
  const totalActiveFilters = useMemo(
    () =>
      filters
        ? filters.reduce(
            (sum, filter) => sum + filter.selectedOptions.length,
            0
          )
        : 0,
    [filters]
  );

  useEffect(() => {
    setPager(
      calculatePager(totalProductsCount, currentPage, {
        pageSize: pageSize,
      })
    );
  }, [totalProductsCount, currentPage, pageSize]);

  useEffect(() => {
    dispatch(queryProductFiltering(null, false, false, true));
  }, []);

  const onPageChanged = (newPageNumber) => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('page', newPageNumber);
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState('WallpaperSimulator', '', newUrl);

    dispatch(queryProductFiltering(null, false, false, true));
  };

  return (
    <div className="relative min-h-[549px] border border-gray-100 bg-gray-50 px-4 py-3">
      <div className="mb-1 flex flex-row justify-between">
        <button
          className="flex items-center py-2"
          onClick={() => setFiltersVisible(!filtersVisible)}
        >
          <Icon
            className="mr-2 h-5 w-5 text-gray-700"
            name="sliders-light"
          />
          <span className="text-sm uppercase">
            {translate('simulator.filter-button-label')}
          </span>
          {totalActiveFilters >= 1 && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700">
              <span className="text-sm leading-tight text-white">
                {totalActiveFilters}
              </span>
            </span>
          )}
        </button>
        <div className="text-sm text-gray-500">
          {products?.length} {translate('simulator.products')}
        </div>
      </div>
      <ActiveFilterChips />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {products &&
          products.length > 0 &&
          products.map((product: Product, idx) =>
            !!product ? (
              <div
                className="flex cursor-pointer flex-col"
                onClick={() => setSelectedProduct(product)}
                key={product.id}
              >
                {product.images && product.images.length > 0 && (
                  <div className="h-full w-full rounded aspect-[3/4]">
                    <ProductCardImageSlider
                      images={product.images}
                      disableSlider={true}
                      forcePatternImage={true}
                      patternImage={product.patternImage}
                    />
                  </div>
                )}
                <div className="px-1 pt-2">
                  <h6>{product.name}</h6>
                </div>
              </div>
            ) : (
              <ProductCardPlaceholder key={'skeleton-' + idx} />
            )
          )}
      </div>
      {pager && (
        <div className="mt-4">
          <Pagination model={pager} onChange={onPageChanged} />
        </div>
      )}
      {filtersVisible && (
        <WallpaperSimulatorFilterModal
          setFiltersVisible={setFiltersVisible}
        />
      )}
    </div>
  );
};

export default WallpaperSimulatorProductSelector;
